import { AlertTriangleIcon } from "lucide-react";
import { Component, type ReactNode, useEffect, useRef, useState } from "react";

import { type MermaidFailureReason, MermaidRenderError, renderMermaid } from "./mermaid-renderer";

export type MermaidRenderState = "loading" | "ready" | "error";

interface MermaidDiagramProps {
  /** Mermaid source to render. Must be non-empty (callers guard empty blocks). */
  code: string;
  /** Notified whenever rendering starts, succeeds, or fails. */
  onRenderStateChange?: (state: MermaidRenderState) => void;
}

type Status =
  | { state: "loading" }
  | { state: "ready"; svg: string }
  | { state: "error"; reason: MermaidFailureReason; detail: string };

function failureSummary(reason: MermaidFailureReason): ReactNode {
  switch (reason) {
    case "invalid":
      return <>This diagram contains invalid Mermaid syntax.</>;
    case "too-large":
    case "timeout":
      return <>This diagram couldn't be rendered within Kairo's size and time limits.</>;
    default:
      return <>This diagram couldn't be rendered.</>;
  }
}

function MermaidRenderFailure({
  reason,
  detail,
}: { reason: MermaidFailureReason; detail?: string }) {
  return (
    <div
      role="alert"
      data-reason={reason}
      className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300"
    >
      <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0">
        <p>{failureSummary(reason)}</p>
        {!!detail && (
          <p className="mt-1 whitespace-pre-wrap break-words font-mono text-xs opacity-80">
            {detail}
          </p>
        )}
      </div>
    </div>
  );
}

function MermaidDiagramInner({ code, onRenderStateChange }: MermaidDiagramProps) {
  const [status, setStatus] = useState<Status>({ state: "loading" });
  const onStateRef = useRef(onRenderStateChange);
  onStateRef.current = onRenderStateChange;

  useEffect(() => {
    let cancelled = false;
    setStatus({ state: "loading" });
    onStateRef.current?.("loading");

    renderMermaid(code)
      .then((svg) => {
        if (cancelled) return;
        setStatus({ state: "ready", svg });
        onStateRef.current?.("ready");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const reason = error instanceof MermaidRenderError ? error.reason : "runtime";
        const detail = error instanceof Error ? error.message : String(error);
        setStatus({ state: "error", reason, detail });
        onStateRef.current?.("error");
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  if (status.state === "loading") {
    return (
      <div
        data-type="mermaid-loading"
        className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400"
      >
        Rendering diagram…
      </div>
    );
  }

  if (status.state === "error") {
    return <MermaidRenderFailure reason={status.reason} detail={status.detail} />;
  }

  return (
    <div
      data-type="mermaid-diagram"
      className="overflow-x-auto [&_svg]:mx-auto [&_svg]:h-auto [&_svg]:max-w-full"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG produced by mermaid with securityLevel "strict" (DOMPurify-sanitized labels, no scripts or foreignObject HTML).
      dangerouslySetInnerHTML={{ __html: status.svg }}
    />
  );
}

interface BoundaryProps {
  children: ReactNode;
  onError?: () => void;
}

/**
 * Isolates unexpected render crashes to the diagram block so a single bad
 * diagram can never take down the surrounding rich-text surface (CR-21).
 */
class MermaidErrorBoundary extends Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onError?.();
  }

  render() {
    if (this.state.failed) {
      return <MermaidRenderFailure reason="runtime" />;
    }
    return this.props.children;
  }
}

/**
 * Renders Mermaid source as an inline SVG diagram with loading and error
 * states. Shared by every Kairo rich-text surface (Tiptap docs, chat and
 * comment markdown).
 */
export function MermaidDiagram({ code, onRenderStateChange }: MermaidDiagramProps) {
  return (
    <MermaidErrorBoundary onError={() => onRenderStateChange?.("error")}>
      <MermaidDiagramInner code={code} onRenderStateChange={onRenderStateChange} />
    </MermaidErrorBoundary>
  );
}
